import { ResultsEmptyState } from './results-empty-state'
import { useFilterContext } from '../../context/filter-context'
import { RESULTS_PAGE_SIZE, usePaginatedWorkouts } from '../../hook/use-paginated-workouts'
import { WorkoutFilter } from '../../service/core-service'
import React, { useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { WorkoutPopup } from 'src/component/programmes/programme-workout-popup'
import { SortByDropdown } from 'src/component/workout-filter/sort-by-dropdown'
import { PaginationControls } from 'src/component/ui/pagination-controls'
import { WorkoutCardList } from 'src/component/ui/workout-card/workout-card-list'
import { WorkoutCardSkeleton } from 'src/component/ui/workout-card/workout-card-skeleton'
import { useSessionContext } from 'src/context/session-context'
import { Workout } from 'src/dao/core-dao'

/**
 * Inline Results view shown on /workouts whenever one or more filters are active. Replaces the
 * retired /workouts-filter-result round-trip and its popular-fallback. Query/pagination/scroll live
 * in usePaginatedWorkouts, shared with the section landing pages.
 */
export const ResultsView = (): React.ReactElement => {
  const { workoutFilter, sort, setSort, hideCompleted, clear, page, setPage } = useFilterContext()
  const { completedWorkoutIds } = useSessionContext()
  const [selectedWorkout, setSelectedWorkout] = useState<Workout | undefined>(undefined)

  // The panel stores equipment selections under `equipment`, but apply them as `equipmentContains`
  // (a "contains" match) so the equipment category behaves as OR per GH-754 — selecting "Barbell"
  // returns workouts that include a barbell, not only barbell-only ones (the subset `equipment`
  // filter would exclude every mixed-equipment workout, e.g. barbell + dumbbell).
  const { equipment, ...filterRest } = workoutFilter ?? {}
  const baseFilter: WorkoutFilter = {
    ...filterRest,
    ...(equipment?.length ? { equipmentContains: equipment } : {}),
    hideCompleted: hideCompleted || undefined,
  }
  const { workouts, totalSize, totalPages, isLoading } = usePaginatedWorkouts(baseFilter, sort, { page, setPage })

  const isEmpty = totalSize === 0 && !isLoading

  return (
    <div className="px-4 py-5">
      <div className="d-flex justify-content-between align-items-end mb-4 flex-wrap gap-2">
        <div>
          <h1 className="font-evogria text-primary text-4xl uppercase mb-1">RESULTS</h1>
          {!isLoading && !isEmpty && (
            <span className="font-roboto-condensed text-primary text-sm uppercase">{totalSize} results found</span>
          )}
        </div>
        {isEmpty ? (
          <span className="font-roboto-condensed text-danger text-sm">No workouts found, please try again!</span>
        ) : (
          <SortByDropdown value={sort} onChange={setSort} />
        )}
      </div>

      {isLoading ? (
        <Row>
          {Array.from({ length: RESULTS_PAGE_SIZE }).map((_, index) => (
            <Col key={index} xs={12} sm={6} lg={4} className="mb-6">
              <WorkoutCardSkeleton />
            </Col>
          ))}
        </Row>
      ) : isEmpty ? (
        <ResultsEmptyState onSeeAll={clear} />
      ) : (
        <>
          <Row>
            {workouts.map((workout) => (
              <Col key={workout.id} xs={12} sm={6} lg={4} className="mb-6">
                <WorkoutCardList
                  workout={workout}
                  isCompleted={completedWorkoutIds.has(workout.id)}
                  onAction={() => setSelectedWorkout(workout)}
                />
              </Col>
            ))}
          </Row>
          <PaginationControls page={page} totalPages={totalPages} onPageChange={setPage} className="mt-4" />
        </>
      )}

      <WorkoutPopup
        setOnClose={() => setSelectedWorkout(undefined)}
        selectedWorkout={selectedWorkout}
        startWorkoutLink={selectedWorkout ? `/workouts/${selectedWorkout.id}` : undefined}
      />
    </div>
  )
}
